import { useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";
import ImageCropModal from "@/components/ImageCropModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Multi-image uploader with per-file crop/zoom/rotate + auto-fit modal.
 * Returns full public URLs to the parent via onChange(urls).
 *
 * Props:
 *   value: string[] (current image URLs)
 *   onChange: (urls: string[]) => void
 *   max: number (default 8)
 *   testIdPrefix: string
 */
export default function ImageUpload({ value = [], onChange, max = 8, testIdPrefix = "img" }) {
  const [uploading, setUploading] = useState(false);
  const [queue, setQueue] = useState([]); // pending File[]
  const [current, setCurrent] = useState(null); // current File under edit
  const inputRef = useRef(null);

  const toFullUrl = (url) => (url.startsWith("http") ? url : `${BACKEND_URL}${url}`);

  const uploadBlob = async (blob, filename) => {
    const fd = new FormData();
    const ext = (blob.type || "image/jpeg").split("/")[1] || "jpg";
    fd.append("file", new File([blob], filename || `photo.${ext}`, { type: blob.type || "image/jpeg" }));
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return toFullUrl(data.url);
  };

  const onFile = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (value.length + files.length > max) {
      toast.error(`Max ${max} images`);
      return;
    }
    const good = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name} is not an image`); continue; }
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} > 8MB`); continue; }
      good.push(f);
    }
    if (!good.length) return;
    setQueue(good.slice(1));
    setCurrent(good[0]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onCropConfirm = async (blob, _preview) => {
    setCurrent(null);
    setUploading(true);
    try {
      const url = await uploadBlob(blob, `photo_${Date.now()}.jpg`);
      onChange([...value, url]);
      toast.success("Photo added");
      // Move to next in queue
      if (queue.length) {
        const [next, ...rest] = queue;
        setQueue(rest);
        setCurrent(next);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onCropCancel = () => {
    setCurrent(null);
    setQueue([]); // cancel all remaining too
  };

  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div data-testid={`${testIdPrefix}-upload`}>
      <label
        className={`inline-flex items-center gap-2 border-2 border-black bg-white px-4 py-2 font-mono-b font-bold uppercase text-sm brutal-shadow-sm ${uploading ? "opacity-60" : "cursor-pointer hover-nudge"}`}
        data-testid={`${testIdPrefix}-upload-label`}
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {uploading ? "UPLOADING..." : `UPLOAD ${max > 1 ? "PHOTOS" : "PHOTO"}`}
        <input
          ref={inputRef}
          type="file"
          data-testid={`${testIdPrefix}-file-input`}
          multiple={max > 1}
          accept="image/*"
          disabled={uploading || !!current}
          onChange={onFile}
          className="hidden"
        />
      </label>
      <p className="text-xs font-mono-b text-black/60 mt-1">
        Max {max} images · JPG/PNG/WEBP · up to 8MB each · you can crop, zoom & fit each one
      </p>
      {value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
          {value.map((img, i) => (
            <div key={img} className="relative border-2 border-black bg-[#F4F4F0]">
              <img src={img} alt="" className="w-full aspect-square object-contain" />
              <button
                type="button"
                data-testid={`${testIdPrefix}-remove-${i}`}
                onClick={() => remove(i)}
                className="absolute top-1 right-1 bg-[#FF007F] text-white w-6 h-6 flex items-center justify-center border-2 border-black"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      {current && (
        <ImageCropModal
          file={current}
          onCancel={onCropCancel}
          onConfirm={onCropConfirm}
        />
      )}
    </div>
  );
}
